'use client';

import { IT_INT } from '@slims/shared/constants';
import { MockBolletteCard, type MockBollettaMeterResult } from '@slims/ui/molecules';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useContextOrg } from '@/hooks/use-context-org';
import type { Meter } from '@/hooks/use-meters';
import { api } from '@/lib/utils/api';

interface MockBollettaResponse {
	result: { written: number };
}

/**
 * Wiring di `MockBolletteCard`: elenca i misuratori della CER selezionata e
 * genera le bollette fittizie uno alla volta, chiamando
 * `/api/meters/:id/mock-bolletta` in sequenza dal browser invece che in un
 * ciclo lato Worker — vedi la nota sulla rotta in `apps/api/src/routes/meters.ts`
 * sul limite di cinquanta subrequest per invocazione.
 */
export function MockBollette() {
	const contextOrg = useContextOrg();
	const orgId = contextOrg?.orgId ?? null;

	const [meters, setMeters] = useState<Meter[]>([]);
	useEffect(() => {
		if (!orgId) {
			setMeters([]);
			return;
		}
		let stale = false;
		api
			.get<Meter[]>('/api/meters', { orgId })
			.then((all) => {
				if (!stale) setMeters(all);
			})
			.catch(() => {
				if (!stale) setMeters([]);
			});
		return () => {
			stale = true;
		};
	}, [orgId]);

	const [busy, setBusy] = useState(false);
	const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
	const [results, setResults] = useState<MockBollettaMeterResult[] | null>(null);

	async function handleGenerate() {
		if (!orgId || meters.length === 0) {
			toast.error('Nessuna configurazione selezionata');
			return;
		}
		setBusy(true);
		setResults(null);
		setProgress({ done: 0, total: meters.length });

		const out: MockBollettaMeterResult[] = [];
		for (const m of meters) {
			try {
				const r = await api.post<MockBollettaResponse>(
					`/api/meters/${m.id}/mock-bolletta?orgId=${orgId}`,
				);
				out.push({ meterId: m.id, codice: m.codice, written: r.result.written });
			} catch (err) {
				out.push({
					meterId: m.id,
					codice: m.codice,
					written: 0,
					error: err instanceof Error ? err.message : 'Errore sconosciuto',
				});
			}
			setProgress((p) => (p ? { done: p.done + 1, total: p.total } : p));
		}

		setResults(out);
		setProgress(null);
		setBusy(false);

		const failed = out.filter((r) => r.error).length;
		const total = out.reduce((s, r) => s + r.written, 0);
		if (failed > 0) toast.warning(`Generazione completata · ${failed} misuratori falliti`);
		else toast.success(`Bollette generate · ${total.toLocaleString('it-IT', IT_INT)} slot`);
	}

	return (
		<MockBolletteCard
			busy={busy}
			meterCount={orgId ? meters.length : null}
			progress={progress}
			results={results}
			onGenerate={() => {
				void handleGenerate();
			}}
		/>
	);
}
